import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { socketClient } from '@/lib/socket'
import { apiClient, api, type BillingsQuery, type BillingsTransactionsQuery } from '@/lib/api'
import type {
  ContractReviewStatus,
  PaginationParams,
  Event,
  EventAttendee,
  ActivityTimelineParams,
  ActivityType,
  Activity,
  HistoryTimelineParams,
  OpportunityHistoryParams,
  FieldSelection,
  Order,
  RenewalTaskTemplate,
  Note,
  ClosedWonMapParams,
  HrEmployeeMapParams,
  BillingsTargetInput,
} from '@/types'

// Integration hooks
export function useIntegrationStatus() {
  return useQuery({
    queryKey: ['integration', 'google', 'status'],
    queryFn: () => apiClient.getIntegrationStatus(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Dashboard hooks
type DashboardParams = { owner_id?: string; startDate?: string; endDate?: string }

export function useDashboardStats(params?: DashboardParams) {
  return useQuery({
    queryKey: ['dashboard', 'stats', params],
    queryFn: () => apiClient.getDashboardStats(params),
  })
}

export function useClosedWonMap(params?: ClosedWonMapParams) {
  return useQuery({
    queryKey: ['closed-won-map', params],
    queryFn: () => apiClient.getClosedWonMap(params),
    // First call warms the geocode cache (can take a few seconds); keep it fresh
    // for a while so re-visits/filter changes are instant (locations are cached).
    staleTime: 5 * 60 * 1000,
  })
}

export function useHrEmployeeMap(params?: HrEmployeeMapParams) {
  return useQuery({
    queryKey: ['hr-employee-map', params],
    queryFn: () => apiClient.getHrEmployeeMap(params),
    // Shares the same geocode cache as the customer map — warm once, fast after.
    staleTime: 5 * 60 * 1000,
  })
}

export function useRecentActivity(limit = 10, params?: DashboardParams) {
  return useQuery({
    queryKey: ['dashboard', 'activity', limit, params],
    queryFn: () => apiClient.getRecentActivity(limit, params),
  })
}

export function useFollowUp(params?: { owner_id?: string }) {
  return useQuery({
    queryKey: ['dashboard', 'follow-up', params?.owner_id],
    queryFn: () => apiClient.getFollowUp(params),
  })
}

export function useRenewalsDue(params?: { owner_id?: string; days?: number }) {
  return useQuery({
    queryKey: ['dashboard', 'renewals-due', params?.owner_id, params?.days],
    queryFn: () => apiClient.getRenewalsDue(params),
  })
}

// Account hooks
export function useAccounts(params?: PaginationParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['accounts', params],
    queryFn: () => apiClient.getAccounts(params),
    enabled: options?.enabled ?? true,
  })
}

export function useAccount(id: string) {
  return useQuery({
    queryKey: ['accounts', id],
    queryFn: () => apiClient.getAccount(id),
    enabled: !!id,
  })
}

/**
 * Ask the backend to read a prospect's website and propose account fields.
 * Writes nothing, so there is no cache to invalidate. The caller owns the
 * AbortController so it can offer a Cancel button.
 */
export function useAccountResearch() {
  return useMutation({
    mutationFn: ({ website, signal }: { website: string; signal?: AbortSignal }) =>
      apiClient.researchAccountWebsite(website, { signal }),
  })
}

// ---------------------------------------------------------------------------
// Sales Trackers (QuickBooks billings) - Admin only
// ---------------------------------------------------------------------------

export function useBillingsFiscalYears(enabled = true) {
  return useQuery({
    queryKey: ['billings', 'fiscal-years'],
    queryFn: () => apiClient.getBillingsFiscalYears(),
    // Gated because this is an admin endpoint: a rep on the self-service transactions
    // page would otherwise fire it and collect a 403 for a list they have their own
    // version of.
    enabled,
  })
}

/**
 * The period is part of every key, not just the fiscal year.
 *
 * Without it, switching from the whole year to September would read the year's answer
 * straight out of the cache and render it under a September heading. React Query has no
 * way to know the request changed if the key does not say so.
 */
const periodKey = (q: BillingsQuery) => [
  q.fy ?? 'latest',
  q.period ?? 'year',
  q.month ?? '',
  q.quarter ?? '',
]

export function useBillingsOverview(query: BillingsQuery = {}) {
  return useQuery({
    queryKey: ['billings', 'overview', ...periodKey(query)],
    queryFn: () => apiClient.getBillingsOverview(query),
  })
}

/**
 * The invoice lines for a period.
 *
 * Every filter is in the key, for the same reason the period is: React Query cannot know
 * the request changed unless the key says so, and a cached unfiltered page rendered under a
 * "Beta Ltd" heading is worse than a slow one.
 */
export function useBillingsTransactions(
  query: BillingsTransactionsQuery = {},
  selfService = false
) {
  return useQuery({
    queryKey: [
      'billings',
      selfService ? 'my-transactions' : 'transactions',
      ...periodKey(query),
      query.page ?? 1,
      query.limit ?? 25,
      query.search ?? '',
      query.rep ?? '',
      query.category ?? '',
      query.product_group ?? '',
      query.house ?? '',
    ],
    queryFn: () =>
      selfService
        ? apiClient.getBillingsMyTransactions(query)
        : apiClient.getBillingsTransactions(query),
    // A rep who is not on the roster gets a 404, which is an answer rather than a fault.
    retry: false,
  })
}

export function useBillingsProducts(query: BillingsQuery = {}) {
  return useQuery({
    queryKey: ['billings', 'products', ...periodKey(query)],
    queryFn: () => apiClient.getBillingsProducts(query),
  })
}

export function useBillingsReps(query: BillingsQuery = {}) {
  return useQuery({
    queryKey: ['billings', 'reps', ...periodKey(query)],
    queryFn: () => apiClient.getBillingsReps(query),
  })
}

export function useBillingsRepDetail(ident?: string, query: BillingsQuery = {}) {
  return useQuery({
    queryKey: ['billings', 'rep', ident, ...periodKey(query)],
    queryFn: () => apiClient.getBillingsRepDetail(ident!, query),
    enabled: !!ident,
  })
}

export function useSetBillingsCategoryActive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { sales_account: string; is_active: boolean }) =>
      apiClient.setBillingsCategoryActive(body),
    // Only the attribution payload filters on is_active. The overview and the matrices
    // are deliberately untouched by retirement, so refetching them would be noise.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billings', 'attribution'] }),
  })
}

/**
 * A rep's own sheet. Separate endpoint from useBillingsRepDetail on purpose: that one
 * is admin-only and takes the rep from the URL, which is why reps cannot be given it.
 */
export function useBillingsMySheet(query: BillingsQuery = {}, enabled = true) {
  return useQuery({
    queryKey: ['billings', 'my-sheet', ...periodKey(query)],
    queryFn: () => apiClient.getBillingsMySheet(query),
    enabled,
    // A rep hitting a 404 is not a rep. Retrying will not change that.
    retry: false,
  })
}

export function useBillingsMyFiscalYears(enabled = true) {
  return useQuery({
    queryKey: ['billings', 'my-fiscal-years'],
    queryFn: () => apiClient.getBillingsMyFiscalYears(),
    enabled,
    retry: false,
  })
}

export function useBillingsWipeImpact(fy?: number, enabled = false) {
  return useQuery({
    queryKey: ['billings', 'wipe-impact', fy],
    queryFn: () => apiClient.getBillingsWipeImpact(fy!),
    // Only asked for when the dialog opens: it is a count over every line in a year.
    enabled: enabled && !!fy,
  })
}

export function useWipeBillingsFiscalYear() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ fy, confirm }: { fy: number; confirm: string }) =>
      apiClient.wipeBillingsFiscalYear(fy, confirm),
    // Everything derived from lines is now wrong, including which years exist at all.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billings'] }),
  })
}

export function useBillingsRepRoster(fy?: number, includeRetired = false, enabled = true) {
  return useQuery({
    queryKey: ['billings', 'rep-roster', fy ?? 'latest', includeRetired],
    queryFn: () => apiClient.getBillingsRepRoster({ fy, include_retired: includeRetired }),
    // Admin-only, so a rep's own transactions page does not ask for it.
    enabled,
  })
}

export function useAddBillingsRep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { rep_label: string; rep_user_id?: string | null; note?: string }) =>
      apiClient.addBillingsRep(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billings', 'rep-roster'] }),
  })
}

export function useSetBillingsRepActive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { roster_id?: number | null; rep_label?: string; is_active: boolean }) =>
      apiClient.setBillingsRepActive(body),
    // Retiring changes no figure, so only the lists that filter on is_active need it.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billings', 'rep-roster'] })
      queryClient.invalidateQueries({ queryKey: ['billings', 'attribution'] })
    },
  })
}

/**
 * `enabled` exists so a page that only needs these when a dialog opens can say so.
 * Attainment already rides along in the overview and rep-detail payloads, so the raw
 * rows are only wanted for editing.
 */
export function useBillingsTargets(fy?: number, scope?: 'company' | 'rep', enabled = true) {
  return useQuery({
    queryKey: ['billings', 'targets', fy ?? 'latest', scope ?? 'all'],
    queryFn: () => apiClient.getBillingsTargets({ fy, scope }),
    enabled,
  })
}

export function useSaveBillingsTargets() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (targets: BillingsTargetInput[]) => apiClient.saveBillingsTargets(targets),
    // The overview carries attainment in its own payload, so it has to be refetched
    // alongside the target list or the card keeps showing the old variance.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billings', 'targets'] })
      queryClient.invalidateQueries({ queryKey: ['billings', 'overview'] })
      queryClient.invalidateQueries({ queryKey: ['billings', 'rep'] })
    },
  })
}

/**
 * The weekly forecast table.
 *
 * Deliberately no polling. The figures are weekly snapshots that only change when the
 * Monday capture runs, so refetching would cost queries to redraw the same numbers - and
 * a figure that shifted mid-meeting would undermine the point of capturing it at all.
 */
export function useBillingsForecast(month?: string, week?: string) {
  return useQuery({
    queryKey: ['billings', 'forecast', month ?? 'current', week ?? 'latest'],
    queryFn: () => apiClient.getBillingsForecast({ month, week }),
  })
}

export function useBillingsImports() {
  return useQuery({
    queryKey: ['billings', 'imports'],
    queryFn: () => apiClient.getBillingsImports(),
  })
}

export function useBillingsAttribution(fy?: number) {
  return useQuery({
    queryKey: ['billings', 'attribution', fy ?? 'all'],
    queryFn: () => apiClient.getBillingsAttribution({ fy }),
  })
}

/** Parse and report. Writes nothing, so there is no cache to invalidate. */
export function usePreviewBillingsImport() {
  return useMutation({
    mutationFn: ({ file, fxToUsd, fxNote }: { file: File; fxToUsd?: string; fxNote?: string }) =>
      apiClient.previewBillingsImport(file, { fxToUsd, fxNote }),
  })
}

/** Preview the Sales Tracker workbook. Writes nothing. */
export function usePreviewBillingsLegacyImport() {
  return useMutation({
    mutationFn: ({ file, fxNa, fxAu }: { file: File; fxNa?: string; fxAu?: string }) =>
      apiClient.previewBillingsLegacyImport(file, { fxNa, fxAu }),
  })
}

export function useCommitBillingsLegacyImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (previewToken: string) => apiClient.commitBillingsLegacyImport(previewToken),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billings'] }),
  })
}

/** Commit invalidates everything: new rows change every figure on every page. */
export function useCommitBillingsImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (previewToken: string) => apiClient.commitBillingsImport(previewToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billings'] })
    },
  })
}

/**
 * Attribution changes are read at query time rather than stored on the line, so
 * saving one has to invalidate every billings figure - not just the queue.
 */
export function useSaveBillingsAlias() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.saveBillingsAlias.bind(apiClient),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billings'] }),
  })
}

export function useSaveBillingsCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.saveBillingsCategory.bind(apiClient),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billings'] }),
  })
}

export function useSaveBillingsOverride() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.saveBillingsOverride.bind(apiClient),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billings'] }),
  })
}

export function useCreateAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createAccount.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof apiClient.updateAccount>[1]
    }) => apiClient.updateAccount(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accounts', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
    },
  })
}

export function useDeleteAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteAccount.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkDeleteAccounts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteAccounts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateAccounts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateAccounts(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkAssignAccounts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ids,
      owner_id,
      notify_owner = true,
    }: {
      ids: string[]
      owner_id: string
      notify_owner?: boolean
    }) => apiClient.bulkAssignAccounts(ids, owner_id, notify_owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Contact hooks
export function useContacts(params?: PaginationParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['contacts', params],
    queryFn: () => apiClient.getContacts(params),
    enabled: options?.enabled ?? true,
  })
}

export function useContact(id: string) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => apiClient.getContact(id),
    enabled: !!id,
  })
}

export function useCreateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createContact.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof apiClient.updateContact>[1]
    }) => apiClient.updateContact(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contacts', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

export function useDeleteContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteContact.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkDeleteContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteContacts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateContacts(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkAssignContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ids,
      owner_id,
      notify_owner = true,
    }: {
      ids: string[]
      owner_id: string
      notify_owner?: boolean
    }) => apiClient.bulkAssignContacts(ids, owner_id, notify_owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Opportunity hooks
export function useOpportunities(params?: PaginationParams) {
  return useQuery({
    queryKey: ['opportunities', params],
    queryFn: () => apiClient.getOpportunities(params),
  })
}

export function useOpportunity(id: string) {
  return useQuery({
    queryKey: ['opportunities', id],
    queryFn: () => apiClient.getOpportunity(id),
    enabled: !!id,
  })
}

export function useOpportunityDetail(id: string) {
  return useQuery({
    queryKey: ['opportunity-detail', id],
    queryFn: () => apiClient.getOpportunityDetail(id),
    enabled: !!id,
  })
}

export function useDealHealth(id: string, enabled = true) {
  return useQuery({
    queryKey: ['deal-health', id],
    queryFn: () => apiClient.getDealHealth(id),
    enabled: !!id && enabled,
  })
}

export function useStageVelocity(id: string, enabled = true) {
  return useQuery({
    queryKey: ['stage-velocity', id],
    queryFn: () => apiClient.getStageVelocity(id),
    enabled: !!id && enabled,
  })
}

export function useWinProbability(id: string, enabled = true) {
  return useQuery({
    queryKey: ['win-probability', id],
    queryFn: () => apiClient.getWinProbability(id),
    enabled: !!id && enabled,
  })
}

export function useAccountHealth(id: string, enabled = true) {
  return useQuery({
    queryKey: ['account-health', id],
    queryFn: () => apiClient.getAccountHealth(id),
    enabled: !!id && enabled,
  })
}

export function useAccountEngagement(id: string, enabled = true) {
  return useQuery({
    queryKey: ['account-engagement', id],
    queryFn: () => apiClient.getAccountEngagement(id),
    enabled: !!id && enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateOpportunity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createOpportunity.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateOpportunity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof apiClient.updateOpportunity>[1]
    }) => apiClient.updateOpportunity(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.id] })
    },
  })
}

export function useSetSyncedQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      opportunityId,
      synced_quote_id,
    }: {
      opportunityId: string
      synced_quote_id: string | null
    }) => {
      const response = await api.put(`/opportunities/${opportunityId}/synced-quote`, {
        synced_quote_id,
      })
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

export function useDeleteOpportunity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteOpportunity.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkDeleteOpportunities() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteOpportunities(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateOpportunities() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateOpportunities(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkAssignOpportunities() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ids,
      owner_id,
      notify_owner = true,
    }: {
      ids: string[]
      owner_id: string
      notify_owner?: boolean
    }) => apiClient.bulkAssignOpportunities(ids, owner_id, notify_owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Lead hooks
export function useLeads(params?: PaginationParams) {
  return useQuery({
    queryKey: ['leads', params],
    queryFn: () => apiClient.getLeads(params),
  })
}

export function useLead(id: string) {
  return useQuery({
    queryKey: ['leads', id],
    queryFn: () => apiClient.getLead(id),
    enabled: !!id,
  })
}

export function useLeadDetail(id: string) {
  return useQuery({
    queryKey: ['lead-detail', id],
    queryFn: () => apiClient.getLeadDetail(id),
    enabled: !!id,
  })
}

export function useLeadScore(id: string, enabled = true) {
  return useQuery({
    queryKey: ['lead-score', id],
    queryFn: () => apiClient.getLeadScore(id),
    enabled: !!id && enabled,
  })
}

export function useCreateLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createLead.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof apiClient.updateLead>[1] }) =>
      apiClient.updateLead(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['lead-detail', variables.id] })
    },
  })
}

export function useDeleteLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteLead.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkDeleteLeads() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteLeads(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateLeads() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateLeads(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkAssignLeads() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ids,
      owner_id,
      notify_owner = true,
    }: {
      ids: string[]
      owner_id: string
      notify_owner?: boolean
    }) => apiClient.bulkAssignLeads(ids, owner_id, notify_owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useConvertLead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      options,
    }: {
      id: string
      options: Parameters<typeof apiClient.convertLead>[1]
    }) => apiClient.convertLead(id, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Task hooks
export function useTasks(params?: PaginationParams) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () => apiClient.getTasks(params),
  })
}

export function useTaskStats() {
  return useQuery({
    queryKey: ['tasks', 'stats'],
    queryFn: () => apiClient.getTaskStats(),
  })
}

export function useTask(id: string) {
  return useQuery({
    queryKey: ['tasks', id],
    queryFn: () => apiClient.getTask(id),
    enabled: !!id,
  })
}

export function useCreateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createTask.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
    },
  })
}

export function useUpdateTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof apiClient.updateTask>[1] }) =>
      apiClient.updateTask(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
    },
  })
}

export function useDeleteTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteTask.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
    },
  })
}

export function useBulkDeleteTasks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteTasks(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateTasks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateTasks(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkAssignTasks() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ids,
      owner_id,
      notify_owner = true,
    }: {
      ids: string[]
      owner_id: string
      notify_owner?: boolean
    }) => apiClient.bulkAssignTasks(ids, owner_id, notify_owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Event hooks
export function useEvents(params?: PaginationParams) {
  return useQuery({
    queryKey: ['events', params],
    queryFn: () => apiClient.getEvents(params),
  })
}

export function useEventStats() {
  return useQuery({
    queryKey: ['events', 'stats'],
    queryFn: () => apiClient.getEventStats(),
  })
}

export function useEventsCalendar(start: string, end: string, ownerId?: string) {
  return useQuery({
    queryKey: ['events', 'calendar', start, end, ownerId],
    queryFn: () => apiClient.getEventsCalendar(start, end),
    enabled: !!start && !!end,
  })
}

export function useEvent(id: string) {
  return useQuery({
    queryKey: ['events', id],
    queryFn: () => apiClient.getEvent(id),
    enabled: !!id,
  })
}

export function useCreateEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createEvent.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, event }: { id: string; event: Partial<Event> }) =>
      apiClient.updateEvent(id, event),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['events', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteEvent.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Event Attendee hooks
export function useEventAttendees(eventId: string) {
  return useQuery({
    queryKey: ['events', eventId, 'attendees'],
    queryFn: () => apiClient.getEventAttendees(eventId),
    enabled: !!eventId,
  })
}

export function useAddEventAttendee(eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (attendee: Partial<EventAttendee>) => apiClient.addEventAttendee(eventId, attendee),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'attendees'] })
      queryClient.invalidateQueries({ queryKey: ['events', eventId] })
    },
  })
}

export function useRemoveEventAttendee(eventId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (relationId: string) => apiClient.removeEventAttendee(eventId, relationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId, 'attendees'] })
      queryClient.invalidateQueries({ queryKey: ['events', eventId] })
    },
  })
}

// Task Relation hooks
export function useAddTaskRelation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, relationId }: { taskId: string; relationId: string }) =>
      apiClient.addTaskRelation(taskId, { relation_id: relationId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.taskId] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useRemoveTaskRelation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, relationId }: { taskId: string; relationId: string }) =>
      apiClient.removeTaskRelation(taskId, relationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.taskId] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useSetTaskPrimaryContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ taskId, relationId }: { taskId: string; relationId: string | null }) =>
      apiClient.setTaskPrimaryContact(taskId, relationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', variables.taskId] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

// User hooks
export function useUsers(params?: PaginationParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['users', params],
    queryFn: () => apiClient.getUsers(params),
    enabled: options?.enabled ?? true,
  })
}

// Call Log hooks
export function useCallLogs(params?: PaginationParams) {
  return useQuery({
    queryKey: ['call-logs', params],
    queryFn: () => apiClient.getCallLogs(params),
  })
}

export function useCallLogStats(params?: { owner_id?: string }) {
  return useQuery({
    queryKey: ['call-logs', 'stats', params],
    queryFn: () => apiClient.getCallLogStats(params),
  })
}

export function useCallLog(id: string) {
  return useQuery({
    queryKey: ['call-logs', id],
    queryFn: () => apiClient.getCallLog(id),
    enabled: !!id,
  })
}

export function useCallLogsByContact(contactId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['call-logs', 'by-contact', contactId, params],
    queryFn: () => apiClient.getCallLogsByContact(contactId, params),
    enabled: !!contactId,
  })
}

export function useCallLogsByAccount(accountId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['call-logs', 'by-account', accountId, params],
    queryFn: () => apiClient.getCallLogsByAccount(accountId, params),
    enabled: !!accountId,
  })
}

export function useCallLogsByLead(leadId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['call-logs', 'by-lead', leadId, params],
    queryFn: () => apiClient.getCallLogsByLead(leadId, params),
    enabled: !!leadId,
  })
}

export function usePhoneLookup(number: string) {
  return useQuery({
    queryKey: ['phone-lookup', number],
    queryFn: () => apiClient.phoneLookup(number),
    enabled: number.length >= 3,
  })
}

export function useCreateCallLog() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createCallLog.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateCallLog() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<Parameters<typeof apiClient.updateCallLog>[1]>
    }) => apiClient.updateCallLog(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['call-logs', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
    },
  })
}

export function useDeleteCallLog() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteCallLog.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkDeleteCallLogs() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.bulkDeleteCallLogs.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
    },
  })
}

// Activity Timeline hooks
export function useActivityTimeline(params: ActivityTimelineParams) {
  return useQuery({
    queryKey: ['activities', 'timeline', params],
    queryFn: () => apiClient.getActivityTimeline(params),
    enabled: !!params.entity_id && !!params.entity_type,
  })
}

export function useUpdateActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      type,
      id,
      updates,
    }: {
      type: ActivityType
      id: string
      updates: Partial<Activity>
    }) => apiClient.updateActivity(type, id, updates),
    onSuccess: () => {
      // Invalidate all timeline queries to refresh
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
      // Also invalidate specific entity type queries
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ type, id }: { type: ActivityType; id: string }) =>
      apiClient.deleteActivity(type, id),
    onSuccess: () => {
      // Invalidate all timeline queries to refresh
      queryClient.invalidateQueries({ queryKey: ['activities', 'timeline'] })
      // Also invalidate specific entity type queries
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Quote hooks
export function useQuotes(params?: PaginationParams) {
  return useQuery({
    queryKey: ['quotes', params],
    queryFn: () => apiClient.getQuotes(params),
  })
}

export function useQuote(id: string) {
  return useQuery({
    queryKey: ['quotes', id],
    queryFn: () => apiClient.getQuote(id),
    enabled: !!id,
  })
}

export function useCreateQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createQuote.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useCreateQuoteFromOpportunity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (opportunityId: string) => {
      const response = await api.post(`/opportunities/${opportunityId}/create-quote`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof apiClient.updateQuote>[1] }) =>
      apiClient.updateQuote(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quotes', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail'] })
    },
  })
}

export function useDeleteQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteQuote.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkDeleteQuotes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteQuotes(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateQuotes() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateQuotes(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useCloneQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.cloneQuote(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useDownloadQuotePdf() {
  return useMutation({
    mutationFn: async (input: string | { id: string; template?: string }) => {
      const id = typeof input === 'string' ? input : input.id
      const template = typeof input === 'string' ? undefined : input.template
      const { blob, engine, fallback } = await apiClient.downloadQuotePdf(id, template)

      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Quote-${id}.pdf` // Filename will be replaced by backend's Content-Disposition header
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      // Surface which engine rendered so the caller can warn on fallback.
      return { engine, fallback }
    },
  })
}

// Quote Line Item hooks
export function useQuoteLineItems(quoteId: string) {
  return useQuery({
    queryKey: ['quote-line-items', quoteId],
    queryFn: () => apiClient.getQuoteLineItems(quoteId),
    enabled: !!quoteId,
  })
}

export function useCreateQuoteLineItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      quoteId,
      data,
    }: {
      quoteId: string
      data: Parameters<typeof apiClient.createQuoteLineItem>[1]
    }) => apiClient.createQuoteLineItem(quoteId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quote-line-items', variables.quoteId] })
      queryClient.invalidateQueries({ queryKey: ['quotes', variables.quoteId] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useUpdateQuoteLineItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      quoteId,
      id,
      data,
    }: {
      quoteId: string
      id: string
      data: Parameters<typeof apiClient.updateQuoteLineItem>[2]
    }) => apiClient.updateQuoteLineItem(quoteId, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quote-line-items', variables.quoteId] })
      queryClient.invalidateQueries({ queryKey: ['quotes', variables.quoteId] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export function useDeleteQuoteLineItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ quoteId, id }: { quoteId: string; id: string }) =>
      apiClient.deleteQuoteLineItem(quoteId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['quote-line-items', variables.quoteId] })
      queryClient.invalidateQueries({ queryKey: ['quotes', variables.quoteId] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

// Opportunity Line Item hooks
export function useOpportunityLineItems(opportunityId: string) {
  return useQuery({
    queryKey: ['opportunity-line-items', opportunityId],
    queryFn: () => apiClient.getOpportunityLineItems(opportunityId),
    enabled: !!opportunityId,
  })
}

export function useCreateOpportunityLineItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      opportunityId,
      data,
    }: {
      opportunityId: string
      data: Parameters<typeof apiClient.createOpportunityLineItem>[1]
    }) => apiClient.createOpportunityLineItem(opportunityId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-line-items', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

export function useUpdateOpportunityLineItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      opportunityId,
      id,
      data,
    }: {
      opportunityId: string
      id: string
      data: Parameters<typeof apiClient.updateOpportunityLineItem>[2]
    }) => apiClient.updateOpportunityLineItem(opportunityId, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-line-items', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

export function useDeleteOpportunityLineItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ opportunityId, id }: { opportunityId: string; id: string }) =>
      apiClient.deleteOpportunityLineItem(opportunityId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-line-items', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

/**
 * Approve or reject a line item discount. Invalidating (rather than reloading
 * the page) keeps the approval row in place, and `isPending` lets the caller
 * disable the buttons — a double-click here used to fire duplicate approvals
 * and duplicate notification emails.
 */
export function useDecideLineItemDiscount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      opportunityId,
      id,
      action,
    }: {
      opportunityId: string
      id: string
      action: 'approve' | 'reject'
    }) => apiClient.decideOpportunityLineItemDiscount(opportunityId, id, action),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-line-items', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

/** Reverse a discount decision, returning the line to 'pending'. */
export function useRevertLineItemDiscount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      opportunityId,
      id,
      reason,
    }: {
      opportunityId: string
      id: string
      reason?: string
    }) => apiClient.revertOpportunityLineItemDiscount(opportunityId, id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-line-items', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

export function useReorderOpportunityLineItems(opportunityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (order: { id: string; sort_order: number }[]) =>
      apiClient.reorderOpportunityLineItems(opportunityId, order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-line-items', opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', opportunityId] })
    },
  })
}

// Opportunity Contact Role hooks
export function useOpportunityContactRoles(opportunityId: string, params?: { role?: string }) {
  return useQuery({
    queryKey: ['opportunity-contact-roles', opportunityId, params],
    queryFn: () => apiClient.getOpportunityContactRoles(opportunityId, params),
    enabled: !!opportunityId,
  })
}

export function useCreateOpportunityContactRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      opportunityId,
      data,
    }: {
      opportunityId: string
      data: Parameters<typeof apiClient.createOpportunityContactRole>[1]
    }) => apiClient.createOpportunityContactRole(opportunityId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-contact-roles', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

export function useUpdateOpportunityContactRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      opportunityId,
      id,
      data,
    }: {
      opportunityId: string
      id: string
      data: Parameters<typeof apiClient.updateOpportunityContactRole>[2]
    }) => apiClient.updateOpportunityContactRole(opportunityId, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-contact-roles', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

export function useDeleteOpportunityContactRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ opportunityId, id }: { opportunityId: string; id: string }) =>
      apiClient.deleteOpportunityContactRole(opportunityId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opportunity-contact-roles', variables.opportunityId],
      })
      queryClient.invalidateQueries({ queryKey: ['opportunity-detail', variables.opportunityId] })
      queryClient.invalidateQueries({ queryKey: ['opportunities'] })
    },
  })
}

// Product hooks
/**
 * Whether the selling pickers should restrict themselves to products that have
 * a list price in the deal's currency.
 *
 * Ships disabled — see backend/src/services/pricingSettings.js for why. Cached
 * for the session: it is one boolean that an admin changes rarely, and every
 * product picker asks for it.
 */
export function useRequirePricedProducts() {
  const { data } = useQuery({
    queryKey: ['pricing-settings'],
    queryFn: () => apiClient.getPricingSettings(),
    staleTime: 5 * 60 * 1000,
  })
  return data?.data?.require_priced_products?.enabled ?? false
}

// ---- Account name aliases --------------------------------------------------

export function useUnresolvedAliases(source?: string) {
  return useQuery({
    queryKey: ['account-aliases', 'unresolved', source],
    queryFn: () => apiClient.getUnresolvedAliases(source),
  })
}

export function useAccountSuggestions(name: string) {
  return useQuery({
    queryKey: ['account-suggestions', name],
    queryFn: () => apiClient.getAccountSuggestions(name),
    enabled: !!name?.trim(),
    // Ranking is deterministic for a given name, so there is no reason to refetch
    // while the user reads the list.
    staleTime: 5 * 60 * 1000,
  })
}

export function useResolveAlias() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, account_id }: { id: number; account_id: string }) =>
      apiClient.resolveAccountAlias(id, account_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-aliases'] })
    },
  })
}

// ---- Training worklist -----------------------------------------------------

export function useTrainingSessions(params?: PaginationParams) {
  return useQuery({
    queryKey: ['training-sessions', params],
    queryFn: () => apiClient.getTrainingSessions(params),
    // Keep the previous rows on screen while a new filter loads. Changing the search,
    // the status or the show-completed toggle produces a new query key, and without
    // this the table unmounts to "Loading..." — destroying and rebuilding up to two
    // thousand Radix Selects for what is usually a sub-second fetch.
    placeholderData: (prev) => prev,
  })
}

export function useTrainingVocabularies() {
  return useQuery({
    queryKey: ['training-vocabularies'],
    queryFn: () => apiClient.getTrainingVocabularies(),
    staleTime: 30 * 60 * 1000,
  })
}

export function useTrainers() {
  return useQuery({
    queryKey: ['training-trainers'],
    queryFn: () => apiClient.getTrainers(),
    staleTime: 10 * 60 * 1000,
  })
}

export function useCreateTrainingSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (
      payload: Partial<import('@/types').TrainingSession> & {
        account_id: string
        course_name: string
      }
    ) => apiClient.createTrainingSession(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-sessions'] })
      queryClient.invalidateQueries({ queryKey: ['training-trainers'] })
    },
  })
}

export function useTrainingStatuses() {
  return useQuery({
    queryKey: ['training-statuses'],
    queryFn: () => apiClient.getTrainingStatuses(),
    // A short vocabulary an admin changes rarely.
    staleTime: 30 * 60 * 1000,
  })
}

export function useUpdateTrainingSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('@/types').TrainingSession> }) =>
      apiClient.updateTrainingSession(id, data),
    onSuccess: () => {
      // Training accounts hold no socket (broadcasts are global and would leak
      // CRM data — see sockets/socketHandler.js), so the worklist relies on this
      // invalidation rather than a real-time event.
      queryClient.invalidateQueries({ queryKey: ['training-sessions'] })
    },
  })
}

/**
 * Importing the tracker workbook. Admin only; the server enforces that.
 *
 * Preview is a mutation rather than a query because it uploads a file and must not be
 * cached or retried on its own.
 */
export function usePreviewTrainingImport() {
  return useMutation({
    mutationFn: (file: File) => apiClient.previewTrainingImport(file),
  })
}

export function useCommitTrainingImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ token, restoreDeleted }: { token: string; restoreDeleted: boolean }) =>
      apiClient.commitTrainingImport(token, restoreDeleted),
    onSuccess: () => {
      // Every Training list is now stale, including the unmatched-name queue.
      queryClient.invalidateQueries({ queryKey: ['training-sessions'] })
      queryClient.invalidateQueries({ queryKey: ['training-worksheet'] })
      queryClient.invalidateQueries({ queryKey: ['training-trainers'] })
      queryClient.invalidateQueries({ queryKey: ['account-aliases'] })
    },
  })
}

/**
 * One of the other five tracker worksheets.
 *
 * Keyed by sheet so the five pages do not share a cache entry, and invalidated the
 * same way sessions are — Training accounts hold no socket, so there is no
 * real-time event to rely on.
 */
export function useTrainingWorksheet(
  sheet: import('@/types').TrainingWorksheetKey,
  params?: PaginationParams
) {
  return useQuery({
    queryKey: ['training-worksheet', sheet, params],
    queryFn: () => apiClient.getTrainingWorksheet(sheet, params),
    // See useTrainingSessions — Onboarding is the widest table in the app.
    placeholderData: (prev) => prev,
  })
}

export function useUpdateTrainingWorksheetRow(sheet: import('@/types').TrainingWorksheetKey) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiClient.updateTrainingWorksheetRow(sheet, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-worksheet', sheet] })
    },
  })
}

export function useProducts(params?: PaginationParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: () => apiClient.getProducts(params),
    enabled: options?.enabled ?? true,
  })
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => apiClient.getProduct(id),
    enabled: !!id,
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createProduct.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof apiClient.updateProduct>[1]
    }) => apiClient.updateProduct(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteProduct.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkDeleteProducts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteProducts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateProducts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateProducts(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateProductPricing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      unit_price,
      currency_iso_code,
    }: {
      id: string
      unit_price: number
      currency_iso_code?: string
    }) => apiClient.updateProductPricing(id, { unit_price, currency_iso_code }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

export function useDeleteProductPricing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteProductPricing(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['products', id] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

// Pricebook hooks
export function usePricebooks(params?: PaginationParams) {
  return useQuery({
    queryKey: ['pricebooks', params],
    queryFn: () => apiClient.getPricebooks(params),
  })
}

export function usePricebook(id: string) {
  return useQuery({
    queryKey: ['pricebooks', id],
    queryFn: () => apiClient.getPricebook(id),
    enabled: !!id,
  })
}

// Bulk Manage Prices editor hooks
export function useBulkPatchPricebookEntries() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      pricebookId,
      payload,
    }: {
      pricebookId: string
      payload: import('@/types').PricebookEntryBulkPatch
    }) => apiClient.bulkPatchPricebookEntries(pricebookId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pricebook-entries', variables.pricebookId] })
      queryClient.invalidateQueries({
        queryKey: ['pricebook-entry-batches', variables.pricebookId],
      })
      queryClient.invalidateQueries({ queryKey: ['pricebooks'] })
    },
  })
}

export function usePricebookEntryBatches(pricebookId: string, limit = 10) {
  return useQuery({
    queryKey: ['pricebook-entry-batches', pricebookId, limit],
    queryFn: () => apiClient.listPricebookEntryBatches(pricebookId, limit),
    enabled: !!pricebookId,
  })
}

export function useRevertPricebookEntryBatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      pricebookId,
      batchId,
      force,
    }: {
      pricebookId: string
      batchId: string
      force?: boolean
    }) => apiClient.revertPricebookEntryBatch(pricebookId, batchId, force),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pricebook-entries', variables.pricebookId] })
      queryClient.invalidateQueries({
        queryKey: ['pricebook-entry-batches', variables.pricebookId],
      })
      queryClient.invalidateQueries({ queryKey: ['pricebooks'] })
    },
  })
}

// Pricebook Entry hooks
export function usePricebookEntries(pricebookId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['pricebook-entries', pricebookId, params],
    queryFn: () => apiClient.getPricebookEntries(pricebookId, params),
    enabled: !!pricebookId,
  })
}

// REMOVED: useCreatePricebookEntry / useUpdatePricebookEntry /
// useDeletePricebookEntry. Their apiClient methods targeted
// `/pricebooks/:id/entries[/:entryId]` routes that do not exist on the backend,
// and no component used them — the Manage Prices editor sends every create,
// update and delete through useBulkPatchPricebookEntries above, which is also
// what produces the revertable PEB batch.

// Order hooks
export function useOrders(params?: PaginationParams) {
  return useQuery({
    queryKey: ['orders', params],
    queryFn: () => apiClient.getOrders(params),
  })
}

export function useOrder(id: string) {
  return useQuery({
    queryKey: ['orders', id],
    queryFn: () => apiClient.getOrder(id),
    enabled: !!id,
  })
}

export function useOrderStats() {
  return useQuery({
    queryKey: ['orders', 'stats'],
    queryFn: () => apiClient.getOrderStats(),
  })
}

export function useOrderItems(orderId: string) {
  return useQuery({
    queryKey: ['order-items', orderId],
    queryFn: () => apiClient.getOrderItems(orderId),
    enabled: !!orderId,
  })
}

export function useCreateOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createOrder.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Order> }) =>
      apiClient.updateOrder(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order-items', variables.id] })
    },
  })
}

export function useBulkUpdateOrders() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateOrders(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteOrder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteOrder.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Additional User hooks
export function useUser(id: string) {
  return useQuery({
    queryKey: ['users', id],
    queryFn: () => apiClient.getUser(id),
    enabled: !!id,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userData: Parameters<typeof apiClient.createUser>[0]) =>
      apiClient.createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      userData,
    }: {
      id: string
      userData: Parameters<typeof apiClient.updateUser>[1]
    }) => apiClient.updateUser(id, userData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      user_type,
    }: {
      id: string
      user_type: Parameters<typeof apiClient.updateUserRole>[1]
    }) => apiClient.updateUserRole(id, user_type),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useActivateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.activateUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['users', id] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useDeleteUserPermanent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteUserPermanent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useBulkUpdateUsers() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateUsers(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useBulkDeleteUsers() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteUsers(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// Email hooks
export function useEmails(
  params?: PaginationParams & {
    incoming?: boolean
    has_attachment?: boolean
    status?: string
    from_date?: string
    to_date?: string
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['emails', params],
    queryFn: () => apiClient.getEmails(params),
    enabled: options?.enabled ?? true,
  })
}

export function useEmail(id: string) {
  return useQuery({
    queryKey: ['emails', id],
    queryFn: () => apiClient.getEmail(id),
    enabled: !!id,
  })
}

export function useEmailThread(id: string) {
  return useQuery({
    queryKey: ['emails', id, 'thread'],
    queryFn: () => apiClient.getEmailThread(id),
    enabled: !!id,
  })
}

export function useEmailStats() {
  return useQuery({
    queryKey: ['emails', 'stats'],
    queryFn: () => apiClient.getEmailStats(),
  })
}

export function useSendEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.sendEmail.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof apiClient.updateEmail>[1] }) =>
      apiClient.updateEmail(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['emails', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

export function useDeleteEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteEmail.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

// Entity-specific email hooks
export function useEmailsByContact(contactId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['emails', 'by-contact', contactId, params],
    queryFn: () => apiClient.getEmailsByContact(contactId, params),
    enabled: !!contactId,
  })
}

export function useEmailsByAccount(accountId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['emails', 'by-account', accountId, params],
    queryFn: () => apiClient.getEmailsByAccount(accountId, params),
    enabled: !!accountId,
  })
}

export function useEmailsByOpportunity(opportunityId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['emails', 'by-opportunity', opportunityId, params],
    queryFn: () => apiClient.getEmailsByOpportunity(opportunityId, params),
    enabled: !!opportunityId,
  })
}

export function useEmailsByLead(leadId: string, params?: PaginationParams) {
  return useQuery({
    queryKey: ['emails', 'by-lead', leadId, params],
    queryFn: () => apiClient.getEmailsByLead(leadId, params),
    enabled: !!leadId,
  })
}

// History Timeline hooks
export function useEntityHistory(params: HistoryTimelineParams) {
  return useQuery({
    queryKey: ['history', 'entity', params],
    queryFn: () => apiClient.getEntityHistory(params),
    enabled: !!params.entity_id,
  })
}

export function useOpportunityHistory(params: OpportunityHistoryParams) {
  return useQuery({
    queryKey: ['history', 'opportunity', params],
    queryFn: () => apiClient.getOpportunityHistory(params),
    enabled: !!params.opportunity_id,
  })
}

export function useHistoryStats(
  entityId: string,
  range?: { start_date?: string; end_date?: string }
) {
  return useQuery({
    queryKey: ['history', 'stats', entityId, range],
    queryFn: () => apiClient.getHistoryStats(entityId, range),
    enabled: !!entityId,
  })
}

// Content/Attachments hooks
export function useContent(params?: import('@/types').ContentQueryParams) {
  return useQuery({
    queryKey: ['content', params],
    queryFn: () => apiClient.getContent(params),
  })
}

export function useContentStats() {
  return useQuery({
    queryKey: ['content', 'stats'],
    queryFn: () => apiClient.getContentStats(),
  })
}

export function useContentById(id: string) {
  return useQuery({
    queryKey: ['content', id],
    queryFn: () => apiClient.getContentById(id),
    enabled: !!id,
  })
}

export function useEntityContent(entityId: string, params?: import('@/types').PaginationParams) {
  return useQuery({
    queryKey: ['content', 'entity', entityId, params],
    queryFn: () => apiClient.getEntityContent(entityId, params),
    enabled: !!entityId,
  })
}

export function useUploadContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.uploadContent.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
      queryClient.invalidateQueries({ queryKey: ['content', 'stats'] })
    },
  })
}

export function useDownloadContent() {
  return useMutation({
    mutationFn: apiClient.downloadContent.bind(apiClient),
  })
}

export function useContentThumbnail(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['content-thumbnail', id],
    queryFn: () => apiClient.getContentThumbnail(id!),
    enabled: !!id && enabled,
    staleTime: 4 * 60 * 1000, // signed URL is valid 5 min; refresh just before
  })
}

export function useContentPreview(id: string | null) {
  return useQuery({
    queryKey: ['content', id, 'preview'],
    queryFn: () => apiClient.getContentPreview(id!),
    enabled: !!id,
  })
}

export function useUpdateContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: import('@/types').UpdateContentData }) =>
      apiClient.updateContent(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['content', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['content'] })
    },
  })
}

export function useDeleteContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteContent.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] })
      queryClient.invalidateQueries({ queryKey: ['content', 'stats'] })
    },
  })
}

export function useLinkContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: import('@/types').LinkContentData }) =>
      apiClient.linkContent(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['content', variables.id] })
      queryClient.invalidateQueries({
        queryKey: ['content', 'entity', variables.data.linked_entity_id],
      })
    },
  })
}

export function useUnlinkContent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, linkedEntityId }: { id: string; linkedEntityId: string }) =>
      apiClient.unlinkContent(id, linkedEntityId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['content', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['content', 'entity', variables.linkedEntityId] })
    },
  })
}

// Repository hooks (curated document library)
type RepositoryListParams = Parameters<typeof apiClient.getRepositoryAssets>[0]

export function useRepositoryAssets(params?: RepositoryListParams) {
  return useQuery({
    queryKey: ['repository', params],
    queryFn: () => apiClient.getRepositoryAssets(params),
  })
}

// ----- Marketing repository --------------------------------------------------
export function useMarketingTagCategories() {
  return useQuery({
    queryKey: ['marketing', 'tag-categories'],
    queryFn: () => apiClient.getMarketingTagCategories(),
    staleTime: 5 * 60 * 1000,
  })
}

export interface MarketingAssetParams {
  q?: string
  tagValueIds?: string[]
  sort?: 'recent' | 'downloads'
  page?: number
  limit?: number
}
export function useMarketingAssets(params?: MarketingAssetParams) {
  return useQuery({
    queryKey: ['marketing', 'assets', params],
    queryFn: () => apiClient.getMarketingAssets(params),
  })
}

export function useCreateMarketingTagValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { category_key: string; value: string }) =>
      apiClient.createMarketingTagValue(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing', 'tag-categories'] }),
  })
}

export function useDeleteMarketingTagValue() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, confirm }: { id: string; confirm?: boolean }) =>
      apiClient.deleteMarketingTagValue(id, confirm),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing'] }),
  })
}

export function useCreateMarketingAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: {
      contentVersionId: string
      title?: string
      description?: string
      tagValueIds: string[]
    }) => apiClient.createMarketingAsset(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing', 'assets'] }),
  })
}

export function useUpdateMarketingAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: { title?: string; description?: string | null; tagValueIds?: string[] }
    }) => apiClient.updateMarketingAsset(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing', 'assets'] }),
  })
}

export function useDeleteMarketingAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteMarketingAsset(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing', 'assets'] }),
  })
}

export function useRepositoryAsset(id: string | undefined) {
  return useQuery({
    queryKey: ['repository', 'detail', id],
    queryFn: () => apiClient.getRepositoryAsset(id!),
    enabled: !!id,
  })
}

export function usePromoteToRepository() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      contentVersionId,
      payload,
    }: {
      contentVersionId: string
      payload: Parameters<typeof apiClient.promoteToRepository>[1]
    }) => apiClient.promoteToRepository(contentVersionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repository'] })
      queryClient.invalidateQueries({ queryKey: ['content'] })
    },
  })
}

export function useUpdateRepositoryAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: Parameters<typeof apiClient.updateRepositoryAsset>[1]
    }) => apiClient.updateRepositoryAsset(id, payload),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['repository'] })
      queryClient.invalidateQueries({ queryKey: ['repository', 'detail', vars.id] })
    },
  })
}

export function useDemoteRepositoryAsset() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.demoteRepositoryAsset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repository'] })
      queryClient.invalidateQueries({ queryKey: ['product-documents'] })
    },
  })
}

export function useLinkRepositoryAssetToProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: Parameters<typeof apiClient.linkRepositoryAssetToProduct>[1]
    }) => apiClient.linkRepositoryAssetToProduct(id, payload),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['repository'] })
      queryClient.invalidateQueries({ queryKey: ['repository', 'detail', vars.id] })
      queryClient.invalidateQueries({ queryKey: ['product-documents', vars.payload.product_id] })
    },
  })
}

export function useUnlinkRepositoryAssetFromProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, linkId }: { id: string; linkId: string }) =>
      apiClient.unlinkRepositoryAssetFromProduct(id, linkId),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['repository'] })
      queryClient.invalidateQueries({ queryKey: ['repository', 'detail', vars.id] })
      queryClient.invalidateQueries({ queryKey: ['product-documents'] })
    },
  })
}

// Product documents (per-product view of the repository)
export function useProductDocuments(productId: string | undefined) {
  return useQuery({
    queryKey: ['product-documents', productId],
    queryFn: () => apiClient.getProductDocuments(productId!),
    enabled: !!productId,
  })
}

export function useAddProductDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      productId,
      payload,
    }: {
      productId: string
      payload: Parameters<typeof apiClient.addProductDocument>[1]
    }) => apiClient.addProductDocument(productId, payload),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['product-documents', vars.productId] })
      queryClient.invalidateQueries({ queryKey: ['repository'] })
    },
  })
}

export function useRemoveProductDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ productId, linkId }: { productId: string; linkId: string }) =>
      apiClient.removeProductDocument(productId, linkId),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['product-documents', vars.productId] })
      queryClient.invalidateQueries({ queryKey: ['repository'] })
    },
  })
}

// Note hooks
export function useNotes(params: { parent_id: string }) {
  return useQuery({
    queryKey: ['notes', 'parent', params.parent_id],
    queryFn: () => apiClient.getNotes(params),
    enabled: !!params.parent_id,
  })
}

export function useNote(id: string) {
  return useQuery({
    queryKey: ['notes', id],
    queryFn: () => apiClient.getNote(id),
    enabled: !!id,
  })
}

export function useCreateNote(parentId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (note: {
      parent_id: string
      title?: string
      body: string
      is_private?: boolean
      owner_id?: string
    }) => apiClient.createNote(note),
    onSuccess: () => {
      if (parentId) {
        queryClient.invalidateQueries({ queryKey: ['notes', 'parent', parentId] })
        queryClient.invalidateQueries({ queryKey: ['lead-detail', parentId] })
        queryClient.invalidateQueries({ queryKey: ['account-detail', parentId] })
        queryClient.invalidateQueries({ queryKey: ['contact-detail', parentId] })
        queryClient.invalidateQueries({ queryKey: ['order', parentId] })
      }
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useUpdateNote(parentId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Note> }) =>
      apiClient.updateNote(id, data),
    onSuccess: () => {
      if (parentId) {
        queryClient.invalidateQueries({ queryKey: ['notes', 'parent', parentId] })
        queryClient.invalidateQueries({ queryKey: ['order', parentId] })
      }
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

export function useDeleteNote(parentId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteNote(id),
    onSuccess: () => {
      if (parentId) {
        queryClient.invalidateQueries({ queryKey: ['notes', 'parent', parentId] })
        queryClient.invalidateQueries({ queryKey: ['order', parentId] })
      }
      queryClient.invalidateQueries({ queryKey: ['notes'] })
    },
  })
}

// Search hooks
export function useGlobalSearch(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['search', 'global', query],
    queryFn: async () => {
      return await apiClient.globalSearch(query, { limit: 10 })
    },
    enabled: options?.enabled !== undefined ? options.enabled : query.length >= 2,
  })
}

export function useSearchSuggestions(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['search', 'suggestions', query],
    queryFn: async () => {
      return await apiClient.getSearchSuggestions(query, 8)
    },
    enabled: options?.enabled !== undefined ? options.enabled : query.length >= 2,
  })
}

// Report hooks
export function useLeadSourceAnalysis(params?: import('@/types').ReportQueryParams) {
  return useQuery({
    queryKey: ['reports', 'leadSourceAnalysis', params],
    queryFn: () => apiClient.getLeadSourceAnalysis(params),
  })
}

export function useSalesForecast(params?: import('@/types').ReportQueryParams) {
  return useQuery({
    queryKey: ['reports', 'salesForecast', params],
    queryFn: () => apiClient.getSalesForecast(params),
  })
}

export function useRevenueMetrics(params?: import('@/types').ReportQueryParams) {
  return useQuery({
    queryKey: ['reports', 'revenueMetrics', params],
    queryFn: () => apiClient.getRevenueMetrics(params),
  })
}

export function usePipelineByStage(params?: {
  owner_id?: string
  startDate?: string
  endDate?: string
}) {
  return useQuery({
    queryKey: ['reports', 'pipelineByStage', params?.owner_id, params?.startDate, params?.endDate],
    queryFn: () => apiClient.getPipelineByStage(params),
  })
}

export function useDaysToClose(params?: import('@/types').ReportQueryParams) {
  return useQuery({
    queryKey: ['reports', 'daysToClose', params],
    queryFn: () => apiClient.getDaysToClose(params),
  })
}

export function useWinLossAnalysis(params?: import('@/types').ReportQueryParams) {
  return useQuery({
    queryKey: ['reports', 'winLossAnalysis', params],
    queryFn: () => apiClient.getWinLossAnalysis(params),
  })
}

export function useTopAccounts(params?: { limit?: number; sortBy?: string; owner_id?: string }) {
  return useQuery({
    queryKey: ['reports', 'topAccounts', params],
    queryFn: () => apiClient.getTopAccounts(params),
  })
}

export function useBigOpportunities(params?: {
  limit?: number
  minAmount?: number
  status?: string
  owner_id?: string
}) {
  return useQuery({
    queryKey: ['reports', 'bigOpportunities', params],
    queryFn: () => apiClient.getBigOpportunities(params),
  })
}

export function useStuckOpportunities(params?: {
  owner_id?: string
  stage_name?: string
  min_tier?: string
  sort?: string
  sort_dir?: string
}) {
  return useQuery({
    queryKey: ['reports', 'stuckOpportunities', params],
    queryFn: () => apiClient.getStuckOpportunities(params),
  })
}

export function useMarketingSingleMonth(params?: { month?: string; owner_id?: string }) {
  return useQuery({
    queryKey: ['reports', 'marketingSingleMonth', params],
    queryFn: () => apiClient.getMarketingSingleMonth(params),
  })
}

export function useMarketingByMonth(params?: {
  startDate?: string
  endDate?: string
  status?: 'pipeline' | 'won'
  owner_id?: string
}) {
  return useQuery({
    queryKey: ['reports', 'marketingByMonth', params],
    queryFn: () => apiClient.getMarketingByMonth(params),
  })
}

export function useMarketingFunnel(params?: {
  startDate?: string
  endDate?: string
  owner_id?: string
}) {
  return useQuery({
    queryKey: ['reports', 'marketingFunnel', params],
    queryFn: () => apiClient.getMarketingFunnel(params),
  })
}

export function useMarketingRevenueFunnel(params?: {
  startDate?: string
  endDate?: string
  owner_id?: string
}) {
  return useQuery({
    queryKey: ['reports', 'marketingRevenueFunnel', params],
    queryFn: () => apiClient.getMarketingRevenueFunnel(params),
  })
}

export function useProductByRevenue(params?: {
  startDate?: string
  endDate?: string
  owner_id?: string
  compare?: boolean
}) {
  return useQuery({
    queryKey: ['reports', 'productByRevenue', params],
    queryFn: () => apiClient.getProductByRevenue(params),
  })
}

export function useProductRevenueTrend(params?: {
  startDate?: string
  endDate?: string
  owner_id?: string
}) {
  return useQuery({
    queryKey: ['reports', 'productRevenueTrend', params],
    queryFn: () => apiClient.getProductRevenueTrend(params),
  })
}

export function useTtmRevenue(params: { category: 'Software' | 'Services'; owner_id?: string }) {
  return useQuery({
    queryKey: ['reports', 'ttmRevenue', params],
    queryFn: () => apiClient.getTtmRevenue(params),
  })
}

export function useMarketingSourceStage(params?: {
  startDate?: string
  endDate?: string
  owner_id?: string
}) {
  return useQuery({
    queryKey: ['reports', 'marketingSourceStage', params],
    queryFn: () => apiClient.getMarketingSourceStage(params),
  })
}

export function useMarketingMqlSqlAcceptance(params?: {
  startDate?: string
  endDate?: string
  owner_id?: string
}) {
  return useQuery({
    queryKey: ['reports', 'marketingMqlSqlAcceptance', params],
    queryFn: () => apiClient.getMarketingMqlSqlAcceptance(params),
  })
}

// Custom Reports hooks
export function useCustomReports() {
  return useQuery({
    queryKey: ['customReports'],
    queryFn: () => apiClient.getCustomReports(),
  })
}

export function useCustomReport(id: string) {
  return useQuery({
    queryKey: ['customReports', id],
    queryFn: () => apiClient.getCustomReport(id),
    enabled: !!id,
  })
}

export function useCreateCustomReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createCustomReport.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customReports'] })
    },
  })
}

export function useUpdateCustomReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: import('@/types').UpdateReportData }) =>
      apiClient.updateCustomReport(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customReports'] })
      queryClient.invalidateQueries({ queryKey: ['customReports', variables.id] })
    },
  })
}

export function useDeleteCustomReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteCustomReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customReports'] })
    },
  })
}

export function useExecuteCustomReport() {
  return useMutation({
    mutationFn: (id: string) => apiClient.executeCustomReport(id),
  })
}

export function useExecuteAdHocReport() {
  return useMutation({
    mutationFn: apiClient.executeAdHocReport.bind(apiClient),
  })
}

export function useReportFields(dataSource: string) {
  return useQuery({
    queryKey: ['reportFields', dataSource],
    queryFn: () => apiClient.getReportFields(dataSource),
    enabled: !!dataSource,
  })
}

export function usePublicReports() {
  return useQuery({
    queryKey: ['custom-reports', 'public'],
    queryFn: () => apiClient.getPublicReports(),
  })
}

export function useShareReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reportId, isPublic }: { reportId: string; isPublic: boolean }) =>
      apiClient.shareReport(reportId, isPublic),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-reports'] })
    },
  })
}

export function useCloneReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reportId, name }: { reportId: string; name?: string }) =>
      apiClient.cloneReport(reportId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-reports'] })
    },
  })
}

export function useToggleReportFavorite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (reportId: string) => apiClient.toggleReportFavorite(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-reports'] })
    },
  })
}

export function useExportReportPdf() {
  return useMutation({
    mutationFn: (reportData: {
      dataSource: string
      config: Record<string, any>
      reportName?: string
    }) => apiClient.exportReportPdf(reportData),
  })
}

export function useDownloadSavedReportPdf() {
  return useMutation({
    mutationFn: (reportId: string) => apiClient.downloadSavedReportPdf(reportId),
  })
}

// Enhancement Requests
export function useEnhancementRequests(
  params?: import('@/types').PaginationParams & {
    status?: string
    priority?: string
    category?: string
    assigned_to?: string
    requested_by?: string
  }
) {
  return useQuery({
    queryKey: ['enhancement-requests', params],
    queryFn: () => apiClient.getEnhancementRequests(params),
  })
}

export function useEnhancementRequest(id: string) {
  return useQuery({
    queryKey: ['enhancement-requests', id],
    queryFn: () => apiClient.getEnhancementRequest(id),
    enabled: !!id,
  })
}

export function useEnhancementRequestStats() {
  return useQuery({
    queryKey: ['enhancement-requests', 'stats'],
    queryFn: () => apiClient.getEnhancementRequestStats(),
  })
}

export function useCreateEnhancementRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createEnhancementRequest.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhancement-requests'] })
    },
  })
}

export function useUpdateEnhancementRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiClient.updateEnhancementRequest(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['enhancement-requests'] })
      queryClient.invalidateQueries({ queryKey: ['enhancement-requests', variables.id] })
    },
  })
}

export function useDeleteEnhancementRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteEnhancementRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enhancement-requests'] })
    },
  })
}

// Google Meet
export function useScheduleMeet() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: {
      title: string
      description?: string
      start_time: string
      end_time: string
      attendee_emails?: string[]
      entity_type?: string
      entity_id?: string
    }) => apiClient.scheduleMeet(params),
    onSuccess: () => {
      // Invalidate events list since a new event was created
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

// Ticket hooks (Support)
export function useTickets(
  params?: PaginationParams & {
    status?: string
    priority?: string
    account_id?: string
    lead_id?: string
  }
) {
  return useQuery({
    queryKey: ['tickets', params],
    queryFn: () => apiClient.getTickets(params),
  })
}

export function useTicketStats() {
  return useQuery({
    queryKey: ['tickets', 'stats'],
    queryFn: () => apiClient.getTicketStats(),
  })
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ['tickets', id],
    queryFn: () => apiClient.getTicket(id),
    enabled: !!id,
  })
}

export function useCreateTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createTicket.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof apiClient.updateTicket>[1]
    }) => apiClient.updateTicket(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}

export function useDeleteTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteTicket.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useAddTicketComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ticketId,
      comment,
    }: {
      ticketId: string
      comment: Parameters<typeof apiClient.addTicketComment>[1]
    }) => apiClient.addTicketComment(ticketId, comment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.ticketId] })
    },
  })
}

export function useUpdateTicketComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ticketId,
      commentId,
      updates,
    }: {
      ticketId: string
      commentId: string
      updates: { body?: string; is_public?: boolean }
    }) => apiClient.updateTicketComment(ticketId, commentId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.ticketId] })
    },
  })
}

export function useDeleteTicketComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, commentId }: { ticketId: string; commentId: string }) =>
      apiClient.deleteTicketComment(ticketId, commentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.ticketId] })
    },
  })
}

export function useBulkDeleteTickets() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => apiClient.bulkDeleteTickets(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useBulkUpdateTickets() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, updates }: { ids: string[]; updates: Record<string, any> }) =>
      apiClient.bulkUpdateTickets(ids, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// Ticket Email Hooks
export function useTicketEmails(ticketId: string) {
  return useQuery({
    queryKey: ['tickets', ticketId, 'emails'],
    queryFn: () => apiClient.getTicketEmails(ticketId),
    enabled: !!ticketId,
  })
}

export function useTicketTasks(ticketId: string) {
  return useQuery({
    queryKey: ['tickets', ticketId, 'tasks'],
    queryFn: () => apiClient.getTicketTasks(ticketId),
    enabled: !!ticketId,
  })
}

export function useCreateTicketTask() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: Partial<import('@/types').Task> }) =>
      apiClient.createTicketTask(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

export function useLinkEmailToTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, emailId }: { ticketId: string; emailId: string }) =>
      apiClient.linkEmailToTicket(ticketId, emailId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.ticketId, 'emails'] })
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

export function useSendTicketEmail() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ticketId,
      email,
    }: {
      ticketId: string
      email: { to: string; subject: string; body: string; html_body?: string }
    }) => apiClient.sendTicketEmail(ticketId, email),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.ticketId] })
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.ticketId, 'emails'] })
    },
  })
}

export function useUnlinkEmailFromTicket() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, emailId }: { ticketId: string; emailId: string }) =>
      apiClient.unlinkEmailFromTicket(ticketId, emailId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tickets', variables.ticketId, 'emails'] })
      queryClient.invalidateQueries({ queryKey: ['emails'] })
    },
  })
}

// ============================================
// Duplicate Detection hooks
// ============================================

export function useDuplicateCheck() {
  return useMutation({
    mutationFn: ({ entityType, data }: { entityType: string; data: Record<string, unknown> }) =>
      apiClient.checkDuplicates(entityType, data),
  })
}

export function useDuplicates(
  entityType: string,
  params?: PaginationParams & { status?: string; confidence_level?: string }
) {
  return useQuery({
    queryKey: ['duplicates', entityType, params],
    queryFn: () => apiClient.getDuplicates(entityType, params),
    enabled: !!entityType,
  })
}

export function useDuplicateStats() {
  return useQuery({
    queryKey: ['duplicates', 'stats'],
    queryFn: () => apiClient.getDuplicateStats(),
  })
}

export function useScanDuplicates() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (entityType: string) => apiClient.scanDuplicates(entityType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicates'] })
    },
  })
}

export function useResolveDuplicate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      apiClient.resolveDuplicate(id, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duplicates'] })
    },
  })
}

// ============================================
// Merge hooks
// ============================================

export function useMergePreview(entityType: string, masterId: string, loserId: string) {
  return useQuery({
    queryKey: ['merge', 'preview', entityType, masterId, loserId],
    queryFn: () => apiClient.getMergePreview(entityType, masterId, loserId),
    enabled: !!entityType && !!masterId && !!loserId,
  })
}

export function useMergeRecords() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      entityType,
      masterId,
      loserId,
      fieldSelections,
    }: {
      entityType: string
      masterId: string
      loserId: string
      fieldSelections: FieldSelection
    }) => apiClient.mergeRecords(entityType, masterId, loserId, fieldSelections),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['duplicates'] })
      queryClient.invalidateQueries({ queryKey: [variables.entityType.toLowerCase() + 's'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

// ============================================
// Renewal Task Template hooks
// ============================================

export function useRenewalTemplates() {
  return useQuery({
    queryKey: ['renewal-templates'],
    queryFn: () => apiClient.getRenewalTemplates(),
    staleTime: 10 * 60 * 1000,
  })
}

export function useCreateRenewalTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (template: Partial<RenewalTaskTemplate>) =>
      apiClient.createRenewalTemplate(template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewal-templates'] })
    },
  })
}

export function useUpdateRenewalTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, updates }: { id: number; updates: Partial<RenewalTaskTemplate> }) =>
      apiClient.updateRenewalTemplate(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewal-templates'] })
    },
  })
}

export function useDeleteRenewalTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => apiClient.deleteRenewalTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewal-templates'] })
    },
  })
}

export function useReorderRenewalTemplates() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (order: { id: number; sort_order: number }[]) =>
      apiClient.reorderRenewalTemplates(order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewal-templates'] })
    },
  })
}

export function useReorderQuoteLineItems(quoteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (order: { id: string; sort_order: number }[]) =>
      apiClient.reorderQuoteLineItems(quoteId, order),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-line-items', quoteId] })
    },
  })
}

// ============================================================
// RFP hooks
// ============================================================

export function useRfps(params?: PaginationParams) {
  return useQuery({
    queryKey: ['rfps', params],
    queryFn: () => apiClient.getRfps(params),
  })
}

export function useRfp(id: string) {
  return useQuery({
    queryKey: ['rfps', id],
    queryFn: () => apiClient.getRfp(id),
    enabled: !!id,
  })
}

export function useRfpPipeline() {
  return useQuery({
    queryKey: ['rfps', 'pipeline'],
    queryFn: () => apiClient.getRfpPipeline(),
  })
}

export function useRfpStats() {
  return useQuery({
    queryKey: ['rfps', 'stats'],
    queryFn: () => apiClient.getRfpStats(),
  })
}

export function useRfpTags() {
  return useQuery({
    queryKey: ['rfps', 'tags'],
    queryFn: () => apiClient.getRfpTags(),
  })
}

export function useCreateRfp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rfp: Partial<import('@/types').Rfp>) => apiClient.createRfp(rfp),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps'] })
    },
  })
}

export function useUpdateRfp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('@/types').Rfp> }) =>
      apiClient.updateRfp(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfps'] })
      queryClient.invalidateQueries({ queryKey: ['rfps', variables.id] })
    },
  })
}

export function useUpdateRfpStage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) =>
      apiClient.updateRfpStage(id, stage),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps'] })
    },
  })
}

export function useDeleteRfp() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteRfp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps'] })
    },
  })
}

export function useCreateRfpTask(rfpId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (task: Partial<import('@/types').RfpTask> & { notify_assignee?: boolean }) =>
      apiClient.createRfpTask(rfpId, task),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps', rfpId] })
    },
  })
}

export function useUpdateRfpTask(rfpId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      taskId,
      data,
    }: {
      taskId: string
      data: Partial<import('@/types').RfpTask> & { notify_assignee?: boolean }
    }) => apiClient.updateRfpTask(rfpId, taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps', rfpId] })
    },
  })
}

export function useDeleteRfpTask(rfpId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (taskId: string) => apiClient.deleteRfpTask(rfpId, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps', rfpId] })
    },
  })
}

export function useAddRfpDocument(rfpId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (doc: Partial<import('@/types').RfpDocument>) =>
      apiClient.addRfpDocument(rfpId, doc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps', rfpId] })
    },
  })
}

export function useDeleteRfpDocument(rfpId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (docId: string) => apiClient.deleteRfpDocument(rfpId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rfps', rfpId] })
    },
  })
}

// Note Session hooks
export function useNoteSessions(params?: {
  q?: string
  status?: string
  who_id?: string
  what_id?: string
  page?: number
  limit?: number
}) {
  return useQuery({
    queryKey: ['note-sessions', params],
    queryFn: () => apiClient.getNoteSessions(params),
  })
}

export function useNoteSession(id: string) {
  return useQuery({
    queryKey: ['note-sessions', id],
    queryFn: () => apiClient.getNoteSession(id),
    enabled: !!id,
  })
}

export function useNoteSessionsByContact(contactId: string) {
  return useQuery({
    queryKey: ['note-sessions', 'by-contact', contactId],
    queryFn: () => apiClient.getNoteSessionsByContact(contactId),
    enabled: !!contactId,
  })
}

export function useNoteSessionsByLead(leadId: string) {
  return useQuery({
    queryKey: ['note-sessions', 'by-lead', leadId],
    queryFn: () => apiClient.getNoteSessionsByLead(leadId),
    enabled: !!leadId,
  })
}

export function useNoteSessionsByOpportunity(opportunityId: string) {
  return useQuery({
    queryKey: ['note-sessions', 'by-opportunity', opportunityId],
    queryFn: () => apiClient.getNoteSessionsByOpportunity(opportunityId),
    enabled: !!opportunityId,
  })
}

export function useNoteSessionsByAccount(accountId: string) {
  return useQuery({
    queryKey: ['note-sessions', 'by-account', accountId],
    queryFn: () => apiClient.getNoteSessionsByAccount(accountId),
    enabled: !!accountId,
  })
}

export function useCreateNoteSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createNoteSession.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note-sessions'] })
    },
  })
}

export function useUpdateNoteSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: import('@/types/noteSession').UpdateNoteSessionInput
    }) => apiClient.updateNoteSession(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['note-sessions', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['note-sessions'] })
    },
  })
}

export function useAutosaveNoteSession() {
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: import('@/types/noteSession').AutosaveNoteSessionInput
    }) => apiClient.autosaveNoteSession(id, data),
  })
}

export function useCompleteNoteSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      notes_content,
      notes_plain_text,
    }: {
      id: string
      notes_content?: string
      notes_plain_text?: string
    }) => apiClient.completeNoteSession(id, { notes_content, notes_plain_text }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['note-sessions', id] })
      queryClient.invalidateQueries({ queryKey: ['note-sessions'] })
    },
  })
}

export function useDeleteNoteSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteNoteSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note-sessions'] })
    },
  })
}

// Note Template hooks
export function useNoteTemplates() {
  return useQuery({
    queryKey: ['note-templates'],
    queryFn: () => apiClient.getNoteTemplates(),
  })
}

export function useNoteTemplate(id: string) {
  return useQuery({
    queryKey: ['note-templates', id],
    queryFn: () => apiClient.getNoteTemplate(id),
    enabled: !!id,
  })
}

export function useCreateNoteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createNoteTemplate.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note-templates'] })
    },
  })
}

export function useUpdateNoteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: import('@/types/noteSession').UpdateNoteTemplateInput
    }) => apiClient.updateNoteTemplate(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['note-templates', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['note-templates'] })
    },
  })
}

export function useDeleteNoteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteNoteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note-templates'] })
    },
  })
}

// ========== HR Module — Phase 1 hooks ==========

export function useEmployees(params?: import('@/types/hr').EmployeeListParams) {
  return useQuery({
    queryKey: ['hr', 'employees', params],
    queryFn: () => apiClient.getEmployees(params),
  })
}

export function useEmployee(id: string) {
  return useQuery({
    queryKey: ['hr', 'employees', id],
    queryFn: () => apiClient.getEmployee(id),
    enabled: !!id,
  })
}

/**
 * The signed-in user's own employee record (`/hr/employees/me`). Available to
 * any authenticated staff member (resolved by user_id or work_email); returns
 * 404 if the account has no linked employee record.
 */
export function useMyEmployeeRecord() {
  return useQuery({
    queryKey: ['hr', 'employees', 'me'],
    queryFn: () => apiClient.getMyEmployeeRecord(),
    retry: false,
  })
}

export function useCreateEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createEmployee.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] })
      queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
    },
  })
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('@/types/hr').Employee> }) =>
      apiClient.updateEmployee(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] })
      queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
    },
  })
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteEmployee.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] })
      queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
    },
  })
}

export function useImportOrgChartCsv() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (csv: string) => apiClient.importOrgChartCsv(csv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] })
      queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
    },
  })
}

export function useImportBrightHr() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (csv: string) => apiClient.importBrightHr(csv),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] })
      queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
    },
  })
}

export function useEmployeeSensitive(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['hr', 'employees', id, 'sensitive'],
    queryFn: () => apiClient.getEmployeeSensitive(id),
    enabled: !!id && enabled,
    retry: false,
  })
}

export function useEmployeeDocuments(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['hr', 'employees', id, 'documents'],
    queryFn: () => apiClient.getEmployeeDocuments(id),
    enabled: !!id && enabled,
  })
}

export function useUploadEmployeeDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      apiClient.uploadEmployeeDocument(id, formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees', variables.id, 'documents'] })
    },
  })
}

export function useDeleteEmployeeDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, docId }: { id: string; docId: string }) =>
      apiClient.deleteEmployeeDocument(id, docId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees', variables.id, 'documents'] })
    },
  })
}

export interface HrContext {
  employeeId: string | null
  isHrAdmin: boolean
  isManager: boolean
  subordinateCount: number
  impersonating?: boolean
}

/** The requester's HR role flags (drives HR nav/visibility). */
export function useHrContext() {
  return useQuery({
    queryKey: ['hr', 'me', 'context'],
    queryFn: () => apiClient.getHrContext(),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCompanyDocuments() {
  return useQuery({
    queryKey: ['hr', 'company-documents'],
    queryFn: () => apiClient.getCompanyDocuments(),
  })
}

export function useUploadCompanyDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ formData }: { formData: FormData }) => apiClient.uploadCompanyDocument(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'company-documents'] })
    },
  })
}

export function useDeleteCompanyDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ docId }: { docId: string }) => apiClient.deleteCompanyDocument(docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'company-documents'] })
    },
  })
}

export function useUpdateEmployeeSensitive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Partial<import('@/types/hr').EmployeeSensitive>
    }) => apiClient.updateEmployeeSensitive(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees', variables.id, 'sensitive'] })
    },
  })
}

// ----- Time off / leave ------------------------------------------------------
export function useTimeOffTypes() {
  return useQuery({
    queryKey: ['hr', 'time-off', 'types'],
    queryFn: () => apiClient.getTimeOffTypes(),
  })
}

export function useTimeOffBalances(params?: { employee_id?: string; year?: number }) {
  return useQuery({
    queryKey: ['hr', 'time-off', 'balances', params],
    queryFn: () => apiClient.getTimeOffBalances(params),
  })
}

export function useTimeOffRequests(params?: { status?: string; scope?: string }) {
  return useQuery({
    queryKey: ['hr', 'time-off', 'requests', params],
    queryFn: () => apiClient.getTimeOffRequests(params),
  })
}

function invalidateTimeOff(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['hr', 'time-off'] })
}

export function useCreateTimeOffRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof apiClient.createTimeOffRequest>[0]) =>
      apiClient.createTimeOffRequest(data),
    onSuccess: () => invalidateTimeOff(queryClient),
  })
}

export function useDecideTimeOffRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string
      decision: 'approve' | 'reject'
      note?: string
    }) => apiClient.decideTimeOffRequest(id, decision, note),
    onSuccess: () => invalidateTimeOff(queryClient),
  })
}

export function useCancelTimeOffRequest() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.cancelTimeOffRequest(id),
    onSuccess: () => invalidateTimeOff(queryClient),
  })
}

export function useUpcomingBirthdays() {
  return useQuery({
    queryKey: ['hr', 'birthdays'],
    queryFn: () => apiClient.getUpcomingBirthdays(),
  })
}

export function useUpcomingAnniversaries() {
  return useQuery({
    queryKey: ['hr', 'anniversaries'],
    queryFn: () => apiClient.getUpcomingAnniversaries(),
  })
}

export function useDepartments(includeInactive = false) {
  return useQuery({
    queryKey: ['hr', 'departments', { includeInactive }],
    queryFn: () => apiClient.getDepartments(includeInactive),
  })
}

export function useDepartment(id: string) {
  return useQuery({
    queryKey: ['hr', 'departments', id],
    queryFn: () => apiClient.getDepartment(id),
    enabled: !!id,
  })
}

export function useCreateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.createDepartment.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'departments'] })
    },
  })
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<import('@/types/hr').Department> }) =>
      apiClient.updateDepartment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'departments'] })
    },
  })
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: apiClient.deleteDepartment.bind(apiClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'departments'] })
    },
  })
}

export function useEmergencyContacts(employeeId: string) {
  return useQuery({
    queryKey: ['hr', 'emergency-contacts', employeeId],
    queryFn: () => apiClient.getEmergencyContacts(employeeId),
    enabled: !!employeeId,
  })
}

export function useCreateEmergencyContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      employeeId,
      data,
    }: {
      employeeId: string
      data: Partial<import('@/types/hr').EmergencyContact>
    }) => apiClient.createEmergencyContact(employeeId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['hr', 'emergency-contacts', variables.employeeId],
      })
    },
  })
}

export function useUpdateEmergencyContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      employeeId,
      id,
      data,
    }: {
      employeeId: string
      id: string
      data: Partial<import('@/types/hr').EmergencyContact>
    }) => apiClient.updateEmergencyContact(employeeId, id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['hr', 'emergency-contacts', variables.employeeId],
      })
    },
  })
}

export function useDeleteEmergencyContact() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ employeeId, id }: { employeeId: string; id: string }) =>
      apiClient.deleteEmergencyContact(employeeId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['hr', 'emergency-contacts', variables.employeeId],
      })
    },
  })
}

export function useOrgChart() {
  return useQuery({
    queryKey: ['hr', 'org-chart'],
    queryFn: () => apiClient.getOrgChart(),
  })
}

// --- Contract reviews ----------------------------------------------------------

/**
 * The review queue. Open reviews by default: the closed ones are the archive, and a queue that
 * fills with signed contracts stops being a queue.
 */
export function useContractReviews(
  params: {
    page?: number
    limit?: number
    status?: string
    account_id?: string
    include_closed?: boolean
    /** Narrow to reviews you raised or are reviewing. The list is team-wide without it. */
    mine?: boolean
  } = {}
) {
  return useQuery({
    /*
     * Every filter must appear here.
     *
     * This key is an explicit list rather than the params object, so a filter added to the
     * signature and forgotten here produces the same key for two different requests: React
     * Query then serves the cached result and the control silently does nothing. `mine` was
     * added to the query string and to this signature and not to this list, so toggling
     * "Only mine" re-rendered the team-wide list it already had.
     */
    queryKey: [
      'contract-reviews',
      params.page ?? 1,
      params.limit ?? 25,
      params.status ?? '',
      params.account_id ?? '',
      params.include_closed ?? false,
      params.mine ?? false,
    ],
    queryFn: () => apiClient.getContractReviews(params),
  })
}

export function useContractReview(id: string | undefined) {
  return useQuery({
    queryKey: ['contract-reviews', id],
    queryFn: () => apiClient.getContractReview(id as string),
    enabled: Boolean(id),
  })
}

export function useContractReviewDocuments(reviewId: string | undefined) {
  return useQuery({
    queryKey: ['contract-reviews', reviewId, 'documents'],
    queryFn: () => apiClient.getContractReviewDocuments(reviewId as string),
    enabled: Boolean(reviewId),
  })
}

export function useUploadContractReviewDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      reviewId,
      file,
      kind,
    }: {
      reviewId: string
      file: File
      kind: 'draft' | 'signature'
    }) => apiClient.uploadContractReviewDocument(reviewId, file, kind),
    onSuccess: (_r, v) => {
      queryClient.invalidateQueries({ queryKey: ['contract-reviews', v.reviewId, 'documents'] })
      // The review itself carries has_signature_file, which gates the signing step, so the
      // detail query has to be refreshed too or the transition stays hidden until a reload.
      queryClient.invalidateQueries({ queryKey: ['contract-reviews', v.reviewId] })
    },
  })
}

export function useDeleteContractReviewDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ reviewId, documentId }: { reviewId: string; documentId: string }) =>
      apiClient.deleteContractReviewDocument(reviewId, documentId),
    onSuccess: (_r, v) => {
      queryClient.invalidateQueries({ queryKey: ['contract-reviews', v.reviewId, 'documents'] })
      queryClient.invalidateQueries({ queryKey: ['contract-reviews', v.reviewId] })
    },
  })
}

export function useCreateContractReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Parameters<typeof apiClient.createContractReview>[0]) =>
      apiClient.createContractReview(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contract-reviews'] }),
  })
}

export function useUpdateContractReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof apiClient.updateContractReview>[1]
    }) => apiClient.updateContractReview(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contract-reviews', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['contract-reviews'] })
    },
  })
}

/**
 * A status change invalidates the detail as well as the list, because it also changes the round
 * count, the status history and which transitions are offered next.
 */
export function useSetContractReviewStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status,
      comment,
    }: {
      id: string
      status: ContractReviewStatus
      comment?: string
    }) => apiClient.setContractReviewStatus(id, status, comment),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contract-reviews', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['contract-reviews'] })
      // The other party is notified server-side; refresh the bell so they see it here too.
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export function useAddContractReviewComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string
      body: string
      comment_type?: string
      is_internal?: boolean
    }) => apiClient.addContractReviewComment(id, payload),
    onSuccess: (_result, variables) =>
      queryClient.invalidateQueries({ queryKey: ['contract-reviews', variables.id] }),
  })
}

export function useDeleteContractReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteContractReview(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contract-reviews'] }),
  })
}

/**
 * Keep contract reviews fresh across tabs and users.
 *
 * Mounted by the queue and the detail page. This is the half tickets never built: the backend
 * emits `ticket:*` events that no frontend code subscribes to, so a ticket changed by one person
 * stays stale for everyone else until they refetch by hand. A redline landing while the other
 * party has the page open is exactly when that matters.
 */
export function useContractReviewRealtime() {
  const queryClient = useQueryClient()
  useEffect(() => {
    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['contract-reviews'] })
    socketClient.onContractReviewChange(invalidate)
    return () => socketClient.offContractReviewChange(invalidate)
  }, [queryClient])
}

/**
 * The welcome-back summary. Fetched only while the dialog is open, once: it is a snapshot of the
 * moment someone logged in, so refetching on focus would only make it jump under their cursor.
 */
export function useWelcomeSummary(
  enabled: boolean,
  window: { client_date: string; day_start: string; day_end: string }
) {
  return useQuery({
    queryKey: ['welcome-summary', window.client_date, window.day_start, window.day_end],
    queryFn: () => apiClient.getWelcomeSummary(window),
    enabled,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
}
